import { useEffect, useRef, type ReactNode } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { SignOut } from "@phosphor-icons/react";
import { Button, Wordmark, cn } from "@ghezaa/ui";
import { ApiError, onSessionChanged } from "../api";
import { returnUrlWithoutMarker, SIGN_IN_MARKER } from "../session";
import { Trans } from "@lingui/react/macro";

export type AuthGateProps<T> = {
  /** The query that identifies the signed-in person: `useAuthUser()` or an app-specific `/api/me`. */
  query: UseQueryResult<T, Error>;
  /** Rendered when the query is unauthenticated (401). */
  signIn: ReactNode;
  appName: string;
  children: (data: T) => ReactNode;
};

/**
 * Loading → blank canvas; 401 → sign-in; other errors → a retry screen; success → the app. Follows the session: when a
 * response says the person's permissions changed in Core, the person and every screen on view are asked again; and a tab
 * coming back to the front asks who is signed in straight away, since the session may have ended meanwhile in another
 * Ghezaa app on this device (a sign-out, someone else signing in), so it shows the sign-in page rather than stale screens.
 */
export function AuthGate<T>({ query, signIn, appName, children }: AuthGateProps<T>) {
  const queryClient = useQueryClient();
  useEffect(() => onSessionChanged(() => void queryClient.invalidateQueries()), [queryClient]);

  const { refetch, isFetching } = query;
  const fetching = useRef(isFetching);
  fetching.current = isFetching;
  useEffect(() => {
    const look = () => {
      if (document.visibilityState === "visible" && !fetching.current) void refetch();
    };
    document.addEventListener("visibilitychange", look);
    return () => document.removeEventListener("visibilitychange", look);
  }, [refetch]);

  // Signed in after a marked round trip (`?signin=auto`): the marker has done its work and leaves the address, so it rides
  // into neither the next sign-in nor a bookmark. A replace, not a navigation: the router keeps its place.
  const { isSuccess } = query;
  useEffect(() => {
    if (!isSuccess || !new URLSearchParams(window.location.search).has(SIGN_IN_MARKER)) return;
    window.history.replaceState(window.history.state, "", returnUrlWithoutMarker() + window.location.hash);
  }, [isSuccess]);

  if (query.isPending) return <div className="min-h-dvh bg-canvas" aria-busy="true" />;

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 401) return <>{signIn}</>;
    return <ErrorScreen appName={appName} message={query.error.message} />;
  }

  return <>{children(query.data)}</>;
}

/**
 * A whole screen that stops the person: the wordmark, what happened, one way on. `inline` puts it in a page instead of
 * the whole window (a `div` without the full height), for Core's /design.
 */
function StopScreen({ appName, title, children, action, inline = false }: { appName: string; title: ReactNode; children: ReactNode; action: ReactNode; inline?: boolean }) {
  const Frame = inline ? "div" : "main";
  return (
    <Frame className={cn("flex flex-col items-start justify-center gap-3 bg-canvas px-6 md:px-10", inline ? "py-10" : "min-h-dvh")}>
      <Wordmark app={appName} className="mb-3" />
      <h1 className="font-display text-3xl text-ink">{title}</h1>
      <p className="max-w-prose text-sm text-ink-2">{children}</p>
      <div className="mt-2">{action}</div>
    </Frame>
  );
}

/** The app could not ask who is signed in (anything but a 401): what went wrong, and Try again. */
export function ErrorScreen({ appName, message, inline }: { appName: string; message: string; inline?: boolean }) {
  return (
    <StopScreen
      appName={appName}
      title={<Trans>{appName} could not load</Trans>}
      inline={inline}
      action={
        <Button onClick={() => window.location.reload()}>
          <Trans>Try again</Trans>
        </Button>
      }
    >
      {message}
    </StopScreen>
  );
}

/**
 * Signed in, but Core grants this person none of the app's permissions: say so instead of a page of refusals. For an app
 * with no page for everyone (Delivery Notes, Sourcing); `onSignOut` is the app's own (Sourcing also forgets this phone).
 */
export function NoAccessScreen({ appName, name, onSignOut, inline }: { appName: string; name: string; onSignOut: () => void; inline?: boolean }) {
  return (
    <StopScreen
      appName={appName}
      title={<Trans>No access to {appName}</Trans>}
      inline={inline}
      action={
        <Button onClick={onSignOut}>
          <SignOut size={16} className="rtl:-scale-x-100" />
          <Trans>Sign out</Trans>
        </Button>
      }
    >
      <Trans>You are signed in as {name}, but none of your roles carries a {appName} permission; ask your manager or IT for access.</Trans>
    </StopScreen>
  );
}
